import {
  TRANSPORT_MESSAGES,
  classifyTransportWith,
} from '../../../shared/api/transportErrorMessages.ts';

/** agent 端传输层文案：rejected 为「联系平台客服」（与 client.ts 既有网络异常文案逐字一致） */
const classifyTransport = classifyTransportWith({
  ...TRANSPORT_MESSAGES,
  rejected: '请求被服务端拒绝，请刷新后重试或联系平台客服',
});
/**
 * 代理商门户 API 失败提示统一映射（让用户明白失败原因，而非直透后端 error）。
 *
 * 数据流契约（对齐本门户 `api/client.ts`）：
 * - `ApiResult.ok=false` 时 `code` 为后端 Result 信封业务码（如 AUTH_403 / 3104 / AGT_404）；
 *   `error` 为后端 message（或 HTTP 提示）。
 * - `code === undefined` 表示传输层失败：error 形态有浏览器网络异常消息
 *   （Chrome "Failed to fetch" / Safari "Load failed" / FF "NetworkError..."）、
 *   'network_error'、'invalid_json'（HTTP ok 但 JSON 解析失败）、`HTTP <status>`、
 *   纯数字 status（无信封 code 的非 2xx）。client 无显式 timeout 机制，
 *   Abort 类消息按超时防御性归类。
 *
 * 收录原则：只收代理商门户实际消费的 API 域（auth / merchants / onboarding /
 * rates / settlement / reconciliation / payout / mfa / materials / notices /
 * reports / sign-witness 等）会返回的高频码。真源
 * `allinks-common/.../ErrorCode.java`（只读摘录），禁止 315 条全量搬运。
 * 未收录 code 一律回退 fallback（后端 message + 码括注，满足 US-201
 * 「文案含业务码或其映射文案」），不吞错、不虚构。
 * agent 无 i18n 体系，用户可见文案硬编码简体中文为门户既有惯例。
 *
 * 跨门户同构：结构与 `web/admin/src/lib/apiErrorMessages.ts` 对齐（形态参考）；
 * 差异点：① 收录面为本门户消费域；② 多常量共用同一字符串码且后端 message
 * 含关键差异的（VAL_400 / 3201 / RECON_422 / APL_422）走前缀合成而非整句覆盖；
 * ③ RATE_LIMIT 按 AUTH-MFA-05 前端契约解析 `RATE_LIMIT:{min}` 剩余分钟。
 */

/** 业务码 → 用户可行动中文（原因 + 下一步） */
const CODE_MESSAGES: Readonly<Record<string, string>> = {
  // ---- 通用层（鉴权 / 限频 / 并发 / 幂等 / 邮箱 / 设备 / 服务可用性）----
  AUTH_401: '登录状态已失效，请重新登录',
  AUTH_401_X: '登录已过期，请重新登录',
  AUTH_401_C: '账号或密码错误，请检查后重试',
  AUTH_401_T: '认证凭证无效，请重新登录',
  AUTH_401_CH: '认证渠道与当前门户不匹配，请从代理商入口登录',
  AUTH_401_D: '账号已被禁用，请联系平台客服',
  AUTH_403: '当前账号无权执行该操作，如需开通请联系平台',
  '42901': '操作过于频繁，请稍后再试',
  CONC_409: '数据已被他人修改，请刷新页面后重试',
  '1009': '该请求与已提交的操作不一致，请刷新页面后基于最新状态重试',
  EMAIL_NOT_VERIFIED: '邮箱尚未验证，请先到登录邮箱完成验证',
  EMAIL_MISSING: '登录邮箱未配置，请联系平台补录',
  DEV_404: '登录设备不存在或已下线，请重新登录',
  SVC_503_STUB: '该功能暂未开放，请联系平台确认',

  // ---- MFA / 2FA（security 页；RATE_LIMIT:{min} 契约在 mapApiError 内特判）----
  '3410': '2FA 验证码错误，请重新输入',
  '3412': '2FA 未启用，请先在安全设置中完成绑定',
  '3413': '2FA 绑定会话已过期，请重新发起绑定',
  '3414': '2FA 已启用，如需重新绑定请先停用',
  MFA_PASSWORD_REQUIRED: '请输入登录密码',
  MFA_PASSWORD_INCORRECT: '登录密码错误，请核对后重试',
  PASSWORD_REQUIRED: '请输入登录密码',
  PASSWORD_INCORRECT: '登录密码错误，请核对后重试',
  // 422 家族已拆码（A5）：按精确码映射，禁止再用裸 422 判断
  CARD_REVEAL_422: '仅已激活的虚拟卡可查看完整卡号',
  MFA_422: '请先绑定 2FA（两步验证）后再执行敏感操作',
  CARD_LIVENESS_422: '该卡片须先完成活体验证（商户强制活体）',

  // ---- 商户 / 代理商（merchants / profile）----
  MCH_404: '商户不存在或已被删除，请刷新列表后重试',
  MCH_409: '商户当前状态不允许该操作，请刷新后查看最新状态',
  AGT_404: '代理商不存在或已被删除，请刷新页面；若持续出现请联系平台客服',
  AGT_422: '代理商当前状态不允许该操作，请联系平台处理账号状态',

  // ---- 开户进件（merchants / onboarding；3201 走前缀合成保留缺项细节）----
  ONB_404: '开户申请不存在或已被处理，请刷新列表后重试',
  '3202': '当日开户申请提交已达上限，请明日再试',
  '3203': '该注册号已存在有效商户，请核对注册号',
  '3205': '该法人 90 天内禁止再次申请开户',
  '3206': '代理商当前状态不可提交开户申请，请联系平台处理',
  '3207': '开户补件次数已达上限，请联系平台客服人工处理',
  '3209': '尚未获得产品授权，请先向平台申请产品授权',
  '3210': '产品尚未配置授权价，请联系平台完成配置后再提交',
  '3211': '邀请链接已失效，请联系代理商重新生成',

  // ---- 费率（rates / authorized-prices / rate-calculator）----
  '3101': '费率低于下层成本价，请上调后重试',
  '3102': '费率超过平台上限，请下调后重试',
  '3103': '费率变更冷却中，请稍后再试',
  '3104': '商户不属于当前代理或状态不可配置费率，请刷新商户列表核对',
  '3105': '产品未授权给当前代理，请先完成产品授权',
  RATE_CALC_400: '费率计算器参数非法（金额/币种），请检查后重试',

  // ---- 金融 / 结算 / 价差 / 提现（settlement 域 / spreads / withdrawals）----
  SPD_404: '价差账户不存在，请联系平台核查分润账户配置',
  SPD_402: '价差可用余额不足，请核对应提金额',
  CMC_404: '佣金账户不存在，请联系平台核查佣金账户配置',
  SET_402: '待结算金额（pending）不足，请核对结算数据',
  SET_404: '结算批次不存在，请刷新列表后重试',
  SET_409: '结算批次状态不允许该操作（或该日批次已完成），请刷新后重试',
  STMT_404: '对账单不存在或无权访问，请刷新后重试',
  '3005': '存在回冲欠款，暂不可提现，请先结清欠款',
  PYM_404: '收款账户不存在或已被删除，请刷新账户列表后重试',
  PYM_403: '收款账户不属于当前代理商，请核对账户归属',
  PYM_409: '收款账户当前状态不允许该操作，请先启用并完成账户验证',

  // ---- 对账 / 回退 / 收款账户申诉（RECON_422 / APL_422 走前缀合成保留差异）----
  RECON_404: '对账结果不存在或已过期，请刷新对账列表后重试',
  REV_404: '回退记录不存在，请刷新列表后重试',
  REV_409: '回退记录当前状态不允许该操作，请刷新后重试',
  APL_409: '该账户已有审核中的变更申诉，请等待审核完成后再操作',

  // ---- 签约见证 / 物料 / 通知 / 报表 / 资质证书 ----
  SW_403: '商户不属于当前代理，不可发起签约见证',
  SW_409: '商户当前状态不允许签约见证，请先完成商户进件',
  MAT_403: '该物料类型不在允许范围，请从物料库重新选择',
  MAT_404: '物料不存在或下载链接已失效，请刷新物料列表后重试',
  NTF_404: '通知不存在或已被撤回，请刷新通知列表',
  RPT_403: '该报表类型未对你开放，如需使用请联系平台开通',
  RPT_404: '报表不存在或生成失败，请重新发起导出',
  QLF_403: '主体资质未通过认证，不可发起资料变更，请先完成资质认证',
  CERT_403: '主体资质未通过认证，暂不可下载证书，请先完成资质认证',
};

/**
 * 前缀合成型：多常量共用同一字符串码、后端 message 含关键差异的码
 * （如 VAL_400 的 234 处调用点各自定制 message「代理提现单笔最低 100」等）。
 * 输出形态：`前缀：后端 message（码）`——整句覆盖会丢具体原因，禁止。
 */
const PREFIX_MESSAGES: Readonly<Record<string, string>> = {
  VAL_400: '请求未通过参数校验',
  VALIDATION_ERROR: '请求未通过参数校验',
  '3201': '开户必填资料缺失',
  RECON_422: '申诉未通过校验',
  APL_422: '变更申诉未提交成功',
};

/** RATE_LIMIT 剩余分钟前缀（AUTH-MFA-05：MFA 限速 message 传剩余分钟，如 RATE_LIMIT:15） */
const RATE_LIMIT_MINUTES_PREFIX = 'RATE_LIMIT:';

/** fallback 已含码时不重复括注；空 fallback 给含码兜底（US-201：文案含业务码） */
function withCodeBracket(text: string, code: string): string {
  if (text.includes(code)) {
    return text;
  }
  return `${text}（${code}）`;
}

/**
 * 统一失败提示入口：
 * - 业务码已收录 → 可行动中文（原因 + 下一步）；
 * - 业务码走前缀合成 → `前缀：后端 message（码）`，保留具体原因；
 * - RATE_LIMIT → 解析 `RATE_LIMIT:{min}` 剩余分钟（AUTH-MFA-05 前端契约）；
 * - 未收录 code → fallback（后端 message）+ 码括注，不吞错不虚构；
 * - code === undefined → 按传输层形态归类（网络异常 / 超时 / 服务不可用 / 未知）。
 *
 * 绝不吞错：所有分支都有输出；fallback 缺失时给通用兜底。
 */
export function mapApiError(code: string | undefined, fallback: string): string {
  if (code !== undefined && code !== '') {
    const mapped = CODE_MESSAGES[code];
    if (mapped) {
      return mapped;
    }
    const base = (fallback ?? '').trim();
    const prefix = PREFIX_MESSAGES[code];
    if (prefix) {
      return base ? withCodeBracket(`${prefix}：${base}`, code) : `${prefix}（${code}）`;
    }
    if (code === 'RATE_LIMIT') {
      if (base.startsWith(RATE_LIMIT_MINUTES_PREFIX)) {
        const minutes = base.slice(RATE_LIMIT_MINUTES_PREFIX.length).trim();
        if (minutes) {
          return `2FA 尝试过于频繁，请在 ${minutes} 分钟后重试`;
        }
      }
      return '操作过于频繁，请稍后再试';
    }
    if (base) {
      return withCodeBracket(base, code);
    }
    return `操作失败（${code}）`;
  }
  const classified = classifyTransport(fallback ?? '');
  if (classified !== TRANSPORT_MESSAGES.unknown) {
    return classified;
  }
  return (fallback ?? '').trim() ? fallback : TRANSPORT_MESSAGES.unknown;
}

/** 仅供测试/守护脚本检视收录面 */
export function apiErrorCodeCount(): number {
  return Object.keys(CODE_MESSAGES).length + Object.keys(PREFIX_MESSAGES).length;
}

/** 仅供测试/守护脚本检视收录面（码表键；守护键 ⊆ ErrorCode.java 码值集合） */
export function apiErrorCodeKeys(): readonly string[] {
  return Object.freeze([
    ...new Set([
      ...Object.keys(CODE_MESSAGES),
      ...Object.keys(PREFIX_MESSAGES),
    ]),
  ]);
}
